import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import { getConfigGet, getConfigPost } from "../getConfig";
import { API } from "../Constant";
import SettingsIcon from "@material-ui/icons/Settings";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "flex-end",
    "& > *": {
      marginRight: theme.spacing(2),
    },
  },
  filterField: {
    minWidth: 180,
  },
  table: {
    marginTop: theme.spacing(2),
  },
  tableHeader: {
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
    fontWeight: "bold",
  },
  logo: {
    width: 60,
    height: 60,
    objectFit: "contain",
  },
  statusChip: {
    fontWeight: "bold",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  circuitOpen: {
    backgroundColor: "#f44336",
    color: "white",
  },
  circuitClosed: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  circuitHalfOpen: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  dialogContent: {
    minWidth: 400,
    paddingTop: "8px !important",
  },
  dialogActions: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
}));

interface Brand {
  id: number;
  brand: string;
  brand_name: string;
  logo_url: string;
  status: string;
  discount: number;
  is_blacklisted: boolean;
  is_sorted: boolean;
  health: {
    circuit_state: string;
    next_test_at: string | null;
    circuit_opened_at: string | null;
    circuit_closed_at: string | null;
    disabled_reason: string | null;
    consecutive_failures: number;
    consecutive_successes: number;
  } | null;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const GiftCardBrands = () => {
  const classes = useStyles();
  const notify = useNotify();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 0,
    has_next_page: false,
    has_previous_page: false,
  });

  // Filters
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("brand_name");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [circuitState, setCircuitState] = useState("");
  const [status, setStatus] = useState("");
  const [isBlacklisted, setIsBlacklisted] = useState("");

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [newCircuitState, setNewCircuitState] = useState("");
  const [reason, setReason] = useState("");
  const [nextTestAt, setNextTestAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const getDefaultNextTestAt = () => {
    return moment().add(10, 'minutes').format('YYYY-MM-DDTHH:mm');
  };

  const getMinDateTime = () => {
    return moment().format('YYYY-MM-DDTHH:mm');
  };

  useEffect(() => {
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.page_size, sortBy, sortOrder]);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        pageSize: pagination.page_size,
        sortBy,
        sortOrder,
      };

      if (search) params.search = search;
      if (circuitState) params.circuit_state = circuitState;
      if (status) params.status = status;
      if (isBlacklisted) params.is_blacklisted = isBlacklisted;

      const config = getConfigGet(params);
      const response: any = await axios.get(
        `${API}/admin/gift-cards/brands`,
        config
      );

      if (response.data.success) {
        setBrands(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Failed to fetch gift card brands",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchBrands();
  };

  const handleClearFilters = () => {
    setSearch("");
    setSortBy("brand_name");
    setSortOrder("ASC");
    setCircuitState("");
    setStatus("");
    setIsBlacklisted("");
    setPagination({ ...pagination, page: 1 });
    setTimeout(() => fetchBrands(), 100);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPagination({ ...pagination, page: newPage + 1 });
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPagination({
      ...pagination,
      page_size: parseInt(event.target.value, 10),
      page: 1,
    });
  };

  const openCircuitStateDialog = (brand: Brand) => {
    setSelectedBrand(brand);
    setNewCircuitState(brand.health?.circuit_state || "");
    setReason("");
    setNextTestAt(getDefaultNextTestAt());
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedBrand(null);
    setNewCircuitState("");
    setReason("");
    setNextTestAt("");
  };

  const handleUpdateCircuitState = async () => {
    if (!selectedBrand || !newCircuitState) {
      notify("Please select a circuit state", "error");
      return;
    }

    setSubmitting(true);
    try {
      const headers = getConfigPost();
      const data = {
        brand: selectedBrand.brand,
        circuit_state: newCircuitState,
        reason: reason || undefined,
        next_test_at: nextTestAt ? moment(nextTestAt).utc().toISOString() : undefined,
      };

      const response: any = await axios.post(
        `${API}/admin/gift-cards/health`,
        data,
        { headers }
      );

      if (response.data.success) {
        notify(
          `Circuit state updated from ${response.data.previous_state} to ${response.data.new_state}`,
          "success"
        );
        handleCloseDialog();
        fetchBrands();
      }
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Failed to update circuit state",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getCircuitStateChip = (circuitState: string | undefined) => {
    if (!circuitState) return <Chip label="N/A" size="small" />;

    const chipClass =
      circuitState === "open"
        ? classes.circuitOpen
        : circuitState === "closed"
        ? classes.circuitClosed
        : classes.circuitHalfOpen;

    return (
      <Chip
        label={circuitState.toUpperCase()}
        size="small"
        className={chipClass}
      />
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ENABLED":
        return "primary";
      case "DISABLED":
        return "secondary";
      case "PAUSED":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <div className={classes.root}>
      <h2>Gift Card Brands</h2>

      {/* Filters */}
      <Paper style={{ padding: 16 }}>
        <div className={classes.filterContainer}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={classes.filterField}
            placeholder="Search by brand name..."
          />

          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterField}
          >
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as string)}
              label="Sort By"
            >
              <MenuItem value="id">ID</MenuItem>
              <MenuItem value="brand_name">Brand Name</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterField}
          >
            <InputLabel>Sort Order</InputLabel>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as "ASC" | "DESC")}
              label="Sort Order"
            >
              <MenuItem value="ASC">Ascending</MenuItem>
              <MenuItem value="DESC">Descending</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterField}
          >
            <InputLabel>Circuit State</InputLabel>
            <Select
              value={circuitState}
              onChange={(e) => setCircuitState(e.target.value as string)}
              label="Circuit State"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="half_open">Half Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterField}
          >
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as string)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ENABLED">Enabled</MenuItem>
              <MenuItem value="DISABLED">Disabled</MenuItem>
              <MenuItem value="PAUSED">Paused</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterField}
          >
            <InputLabel>Blacklisted</InputLabel>
            <Select
              value={isBlacklisted}
              onChange={(e) => setIsBlacklisted(e.target.value as string)}
              label="Blacklisted"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </Button>

          <Button variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </div>
      </Paper>

      {/* Table */}
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <Paper className={classes.table}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>ID</TableCell>
                <TableCell className={classes.tableHeader}>Logo</TableCell>
                <TableCell className={classes.tableHeader}>Brand</TableCell>
                <TableCell className={classes.tableHeader}>
                  Brand Name
                </TableCell>
                <TableCell className={classes.tableHeader}>Status</TableCell>
                <TableCell className={classes.tableHeader}>
                  Discount %
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  Blacklisted
                </TableCell>
                <TableCell className={classes.tableHeader}>Sorted</TableCell>
                <TableCell className={classes.tableHeader}>
                  Circuit State
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  Disabled Reason
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  Next Test At
                </TableCell>
                <TableCell className={classes.tableHeader}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brands.map((brand) => (
                <TableRow key={brand.id}>
                  <TableCell>{brand.id}</TableCell>
                  <TableCell>
                    <Avatar
                      src={brand.logo_url}
                      alt={brand.brand_name}
                      className={classes.logo}
                      variant="square"
                    />
                  </TableCell>
                  <TableCell>{brand.brand}</TableCell>
                  <TableCell>{brand.brand_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={brand.status}
                      color={getStatusColor(brand.status)}
                      size="small"
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell>{brand.discount}%</TableCell>
                  <TableCell>
                    <Chip
                      label={brand.is_blacklisted ? "Yes" : "No"}
                      color={brand.is_blacklisted ? "secondary" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={brand.is_sorted ? "Yes" : "No"}
                      color={brand.is_sorted ? "primary" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {getCircuitStateChip(brand.health?.circuit_state)}
                  </TableCell>
                  <TableCell>
                    {brand.health?.disabled_reason || "N/A"}
                  </TableCell>
                  <TableCell>
                    {brand.health?.next_test_at
                      ? new Date(brand.health.next_test_at).toLocaleString()
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={brand.status === "ENABLED" ? "Update Circuit State" : "Only enabled brands circuit state can be updated"}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openCircuitStateDialog(brand)}
                          disabled={brand.status !== "ENABLED"}
                        >
                          <SettingsIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            count={pagination.total}
            page={pagination.page - 1}
            onChangePage={handlePageChange}
            rowsPerPage={pagination.page_size}
            onChangeRowsPerPage={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* Circuit State Update Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Update Circuit State</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <p>
            <strong>Brand:</strong> {selectedBrand?.brand_name}
          </p>
          <p>
            <strong>Current State:</strong>{" "}
            {selectedBrand?.health?.circuit_state || "N/A"}
          </p>

          <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel>New Circuit State</InputLabel>
            <Select
              value={newCircuitState}
              onChange={(e) => setNewCircuitState(e.target.value as string)}
              label="New Circuit State"
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="half_open">Half Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Next Test At"
            variant="outlined"
            type="datetime-local"
            value={nextTestAt}
            onChange={(e) => setNextTestAt(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: getMinDateTime(),
            }}
            helperText="When the next health check test should happen (default: 10 mins from now)"
          />

          <TextField
            fullWidth
            margin="normal"
            label="Reason (Optional)"
            variant="outlined"
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for circuit state change..."
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateCircuitState}
            color="primary"
            variant="contained"
            disabled={submitting || !newCircuitState}
          >
            {submitting ? <CircularProgress size={24} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GiftCardBrands;
